import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Handshake, Plus, ChevronRight, Search, Trash2, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { getSessions, getSessionStats, getPendingSessions, searchUsers, createSession, watchSplitSession, notifyGuestSign, DOC_TYPES } from "@/lib/afmApi";
import { fmtDate } from "@/lib/format";
import { Loading, EmptyState, ErrorState, SectionTitle } from "@/components/common";
import { StatusBadge, SESSION_STATUS } from "@/components/StatusBadge";
import { SplitsShowcase } from "@/components/SplitsShowcase";

const CONTRIB = {
  publishing: ["Compositor", "Letrista", "Arreglista"],
  master: ["Intérprete", "Featured", "Productor", "Mezclador"],
  integrated: ["Compositor", "Letrista", "Arreglista", "Intérprete", "Featured", "Productor", "Mezclador"],
};
const DOC_DESC = {
  integrated: "Master + Publishing en un solo contrato.",
  master: "Release de Intérprete · cesión del Máster al Productor.",
  publishing: "Songwriter Split · derechos de autor (composición).",
};

const emptyParticipant = (u) => ({
  user_id: u.id,
  name: u.name,
  email: u.email,
  id_document: "",
  role: u.role || "artist",
  contribution_role: "",
  rights: "both",
  master_pct: 0,
  publishing_pct: 0,
});

function CreateDialog({ open, onClose, onCreated }) {
  const { user } = useAuth();
  const [title, setTitle] = useState("");
  const [type, setType] = useState("single");
  const [docType, setDocType] = useState("integrated");
  const [producer, setProducer] = useState({ is_label: false, company: "", representative: "", rep_document: "", tax_id: "", country: "VE", email: "", master_pct: 0 });
  const [isrc, setIsrc] = useState("");
  const [iswc, setIswc] = useState("");
  const [fonogramaTitle, setFonogramaTitle] = useState("");
  const [jurisdiction, setJurisdiction] = useState("Venezuela");
  const [recoupment, setRecoupment] = useState(false);
  const [recoupNote, setRecoupNote] = useState("");
  const [syncApproved, setSyncApproved] = useState(false);
  const [autofilled, setAutofilled] = useState(false);
  const [legalOpen, setLegalOpen] = useState(null);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [location, setLocation] = useState("Remoto");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [saving, setSaving] = useState(false);
  const [extEmail, setExtEmail] = useState("");
  const [extName, setExtName] = useState("");

  const isEmail = (v) => /^\S+@\S+\.\S+$/.test((v || "").trim());

  useEffect(() => {
    if (open && user) {
      let saved = null;
      try { saved = JSON.parse(localStorage.getItem(`afm_legal_${user.id}`) || "null"); } catch { saved = null; }
      const cl = saved || {};
      setParticipants([{
        ...emptyParticipant({ id: user.id, name: user.name, email: cl.email || user.email, role: user.role }),
        id_document: cl.id_document || user.id_document || "",
        nombre_artistico: cl.nombre_artistico || user.artist_stage_name || "",
        tax_id: cl.tax_id || "", razon_social: cl.razon_social || "",
        country: cl.country || "", address: cl.address || "",
        publisher: cl.publisher || "", ipi_autor: cl.ipi_autor || "",
        ipi_editora: cl.ipi_editora || "", pro: cl.pro || "",
        master_pct: 100, publishing_pct: 100,
      }]);
      setAutofilled(!!saved);
      setTitle(""); setType("single"); setDocType("integrated"); setQ(""); setResults([]); setExtEmail(""); setExtName(""); setLegalOpen(null);
      setProducer({ is_label: false, company: "", representative: user.name || "", rep_document: "", tax_id: "", country: "VE", email: user.email || "", master_pct: 0 });
      setIsrc(""); setIswc(""); setFonogramaTitle(""); setJurisdiction("Venezuela"); setRecoupment(false); setRecoupNote(""); setSyncApproved(false);
    }
  }, [open, user]);

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(() => {
      searchUsers(q.trim()).then(setResults).catch(() => setResults([]));
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const addParticipant = (u) => {
    if (participants.some((p) => p.user_id === u.id)) return;
    setParticipants((prev) => [...prev, emptyParticipant(u)]);
    setQ(""); setResults([]);
  };

  const addExternalInline = () => {
    const text = q.trim();
    if (text.length < 2) return;
    const email = isEmail(text) ? text : "";
    if (email && participants.some((p) => p.email === email)) {
      toast.error("Ese colaborador ya está en la lista");
      setQ(""); setResults([]); return;
    }
    setParticipants((prev) => [...prev, {
      user_id: `ext_${Math.random().toString(36).slice(2, 9)}`, name: text, email, id_document: "",
      role: "producer", contribution_role: "", rights: "both", master_pct: 0, publishing_pct: 0, is_external: true,
    }]);
    setQ(""); setResults([]);
    toast.success("Colaborador externo añadido · invitado (pendiente de firma)");
  };

  const addExternalByEmail = () => {
    const email = extEmail.trim();
    if (!isEmail(email)) { toast.error("Ingresa un correo válido"); return; }
    if (participants.some((p) => p.email === email)) { toast.error("Ese colaborador ya está en la lista"); setExtEmail(""); setExtName(""); return; }
    setParticipants((prev) => [...prev, {
      user_id: `ext_${Math.random().toString(36).slice(2, 9)}`, name: extName.trim() || email, email, id_document: "",
      role: "producer", contribution_role: "", rights: "both", master_pct: 0, publishing_pct: 0, is_external: true,
    }]);
    setExtEmail(""); setExtName("");
    toast.success("Colaborador externo añadido · recibirá un correo para firmar");
  };

  const updateP = (idx, field, value) => {
    setParticipants((prev) => prev.map((p, i) => {
      if (i !== idx) return p;
      const np = { ...p, [field]: value };
      if (field === "rights") {
        if (value === "master") np.publishing_pct = 0;
        if (value === "publishing") np.master_pct = 0;
      }
      return np;
    }));
  };

  const removeP = (idx) => setParticipants((prev) => prev.filter((_, i) => i !== idx));

  const showMaster = docType === "master" || docType === "integrated";
  const showPub = docType === "publishing" || docType === "integrated";
  const hasMaster = (p) => showMaster && (p.rights === "both" || p.rights === "master");
  const hasPub = (p) => showPub && (p.rights === "both" || p.rights === "publishing");
  const masterParties = participants.filter(hasMaster);
  const pubParties = participants.filter(hasPub);
  const producerMaster = showMaster ? Number(producer.master_pct || 0) : 0;
  const masterSum = masterParties.reduce((a, p) => a + Number(p.master_pct || 0), 0) + producerMaster;
  const pubSum = pubParties.reduce((a, p) => a + Number(p.publishing_pct || 0), 0);
  const masterActive = masterParties.length > 0 || producerMaster > 0;
  const masterOk = !masterActive || masterSum === 100;
  const pubOk = pubParties.length === 0 || pubSum === 100;
  const titleOk = title.trim().length >= 3;
  const internals = participants.filter((p) => !p.is_external);
  const internalsOk = internals.every((p) => (p.id_document || "").trim() && isEmail(p.email));
  const valid = titleOk && participants.length >= 1 && masterOk && pubOk && (masterActive || pubParties.length > 0) && internalsOk;

  const submit = async () => {
    if (!titleOk) { toast.error("El título del proyecto debe tener al menos 3 caracteres."); return; }
    const missing = internals.find((p) => !(p.id_document || "").trim() || !isEmail(p.email));
    if (missing) { toast.error(`Falta el número de documento y/o correo de ${missing.name || "un participante"}.`); return; }
    if (!masterOk || !pubOk) { toast.error("Los porcentajes deben sumar 100% (Master y/o Publishing según el tipo)."); return; }
    if (!valid) { toast.error("Revisa los datos del acuerdo."); return; }
    setSaving(true);
    try {
      const LEGAL = ["id_document", "nombre_artistico", "razon_social", "tax_id", "country", "address", "publisher", "ipi_autor", "ipi_editora", "pro"];
      const legal = (p) => Object.fromEntries(LEGAL.map((k) => [k, p[k] || ""]));
      const payload = {
        creator_id: user.id,
        project_title: title.trim(),
        project_type: type,
        doc_type: docType,
        fonograma_title: fonogramaTitle,
        jurisdiction,
        isrc,
        iswc,
        recoupment,
        recoup_note: recoupNote,
        sync_approved: syncApproved,
        producer: showMaster ? { ...producer, master_pct: Number(producer.master_pct) || 0, signer_id: user.id } : undefined,
        session_date: date,
        location,
        participants: participants.map((p) => ({
          user_id: p.user_id, external: !!p.is_external, name: p.name, email: p.email,
          role: p.role, contribution_role: p.contribution_role || "", rights: p.rights,
          master_pct: Number(p.master_pct), publishing_pct: Number(p.publishing_pct), ...legal(p),
        })),
      };
      try {
        const me = participants.find((p) => p.user_id === user.id);
        if (me) localStorage.setItem(`afm_legal_${user.id}`, JSON.stringify({ email: me.email, id_document: me.id_document, nombre_artistico: me.nombre_artistico, tax_id: me.tax_id, razon_social: me.razon_social, country: me.country, address: me.address, publisher: me.publisher, ipi_autor: me.ipi_autor, ipi_editora: me.ipi_editora, pro: me.pro }));
      } catch { /* noop */ }
      const res = await createSession(payload);
      if (res?.id) watchSplitSession({ session_id: res.id, creator_id: user.id, project_title: title.trim() }).catch(() => {});
      const guests = (res?.participants || []).filter((p) => p.external && p.guest_token && p.email);
      guests.forEach((g) => notifyGuestSign({ email: g.email, name: g.name, project_title: title.trim(), inviter: user.name || "", token: g.guest_token, doc_type: docType, master_pct: g.master_pct, publishing_pct: g.publishing_pct }).catch(() => {}));
      toast.success(guests.length ? "Acuerdo creado · invitación enviada por correo a los colaboradores externos" : "Acuerdo creado");
      onCreated(res);
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x.msg || JSON.stringify(x)).join("; ") : (d ? JSON.stringify(d) : "No se pudo crear el acuerdo");
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;
  const inputCls = "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-black";

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4" data-testid="create-session-dialog">
      <div className="my-8 w-full max-w-2xl rounded-2xl border border-slate-300 bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h2 className="font-serif text-xl font-bold text-black">Nuevo acuerdo</h2>
          <button onClick={onClose} data-testid="close-create-dialog"><X className="h-5 w-5 text-slate-400 hover:text-black" /></button>
        </div>
        <div className="max-h-[70vh] space-y-5 overflow-y-auto p-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-600">Título del proyecto</label>
              <input data-testid="session-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ej. Luces de Neón" className={inputCls} />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-600">Tipo de documento</label>
              <select value={docType} onChange={(e) => setDocType(e.target.value)} className={inputCls} data-testid="session-doctype-select">
                {Object.entries(DOC_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <p className="mt-1 text-[11px] text-slate-400">{DOC_DESC[docType]}</p>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-600">Tipo</label>
              <select value={type} onChange={(e) => setType(e.target.value)} className={inputCls} data-testid="session-type-select">
                <option value="single">Single</option>
                <option value="ep">EP</option>
                <option value="album">Álbum</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-600">Fecha</label>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-600">Ubicación</label>
              <input value={location} onChange={(e) => setLocation(e.target.value)} className={inputCls} />
            </div>
          </div>

          {/* Metadatos del documento (opcionales) */}
          <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4" data-testid="doc-meta-section">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-600">Metadatos del documento <span className="font-normal normal-case text-slate-400">· opcionales</span></div>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {showMaster && <input data-testid="doc-isrc" value={isrc} onChange={(e) => setIsrc(e.target.value)} placeholder="ISRC del Máster (VE-ABC-26-00001)" className={inputCls} />}
              {showPub && <input data-testid="doc-iswc" value={iswc} onChange={(e) => setIswc(e.target.value)} placeholder="ISWC de la Composición (T-123456789-0)" className={inputCls} />}
              {showMaster && <input data-testid="doc-fonograma" value={fonogramaTitle} onChange={(e) => setFonogramaTitle(e.target.value)} placeholder="Título del fonograma (opcional)" className={inputCls} />}
              <input data-testid="doc-jurisdiction" value={jurisdiction} onChange={(e) => setJurisdiction(e.target.value)} placeholder="Jurisdicción" className={inputCls} />
            </div>
            <div className="mt-3 space-y-2">
              {showMaster && (
                <label className="flex items-start gap-2 text-sm text-slate-700" data-testid="doc-recoupment-toggle">
                  <input type="checkbox" checked={recoupment} onChange={(e) => setRecoupment(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-slate-300" />
                  <span>Recuperar costos antes de repartir el Máster</span>
                </label>
              )}
              {showMaster && recoupment && (
                <input data-testid="doc-recoup-note" value={recoupNote} onChange={(e) => setRecoupNote(e.target.value)} placeholder="Detalle de costos a recuperar (opcional)" className={inputCls} />
              )}
              <label className="flex items-start gap-2 text-sm text-slate-700" data-testid="doc-sync-toggle">
                <input type="checkbox" checked={syncApproved} onChange={(e) => setSyncApproved(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-slate-300" />
                <span>Autorizar sincronización (cine / TV / videojuegos)</span>
              </label>
            </div>
          </div>

          {showMaster && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="producer-section">
              <div className="flex items-center justify-between">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-600">Productor fonográfico (parte contratante)</div>
                <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={producer.is_label} onChange={(e) => setProducer({ ...producer, is_label: e.target.checked })} data-testid="producer-is-label" /> Es sello / empresa</label>
              </div>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                {producer.is_label && <input data-testid="producer-company" value={producer.company} onChange={(e) => setProducer({ ...producer, company: e.target.value })} placeholder="Razón social del sello" className={inputCls} />}
                <input data-testid="producer-representative" value={producer.representative} onChange={(e) => setProducer({ ...producer, representative: e.target.value })} placeholder={producer.is_label ? "Representante legal" : "Nombre completo"} className={inputCls} />
                <input data-testid="producer-rep-document" value={producer.rep_document} onChange={(e) => setProducer({ ...producer, rep_document: e.target.value })} placeholder="Cédula / Pasaporte" className={inputCls} />
                <input data-testid="producer-tax-id" value={producer.tax_id} onChange={(e) => setProducer({ ...producer, tax_id: e.target.value })} placeholder="RIF / Tax ID" className={inputCls} />
                <input data-testid="producer-country" value={producer.country} onChange={(e) => setProducer({ ...producer, country: e.target.value })} placeholder="País (ISO: VE, CO, US…)" className={inputCls} />
                <input data-testid="producer-email" type="email" value={producer.email} onChange={(e) => setProducer({ ...producer, email: e.target.value })} placeholder="Email del productor" className={inputCls} />
                <input data-testid="producer-master-pct" type="number" min="0" max="100" value={producer.master_pct} onChange={(e) => setProducer({ ...producer, master_pct: e.target.value })} placeholder="% Máster del productor (opcional)" className={inputCls} />
              </div>
            </div>
          )}
          {/* Search collaborators */}
          <div className="relative">
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-600">Añadir colaborador</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input data-testid="collaborator-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre o email…" className={`${inputCls} pl-9`} />
            </div>
            {q.trim().length >= 2 && (
              <div className="absolute z-10 mt-1 w-full overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg" data-testid="collab-dropdown">
                {results.map((u) => (
                  <button key={u.id} onClick={() => addParticipant(u)} data-testid={`collab-result-${u.id}`} className="flex w-full items-center justify-between px-4 py-2.5 text-left text-sm hover:bg-slate-50">
                    <div><div className="font-medium text-black">{u.name}</div><div className="text-xs text-slate-500">{u.email}</div></div>
                    <Plus className="h-4 w-4 text-slate-400" />
                  </button>
                ))}
                {(results.length === 0 || isEmail(q)) && (
                  <button type="button" data-testid="add-external-option" onClick={addExternalInline} className="flex w-full items-center gap-2 border-t border-slate-100 px-4 py-2.5 text-left text-sm font-semibold text-[#AC3333] hover:bg-[#AC3333]/5">
                    <Plus className="h-4 w-4 shrink-0" /> Añadir "{q.trim()}" como colaborador externo
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Add external collaborator by email (recibe un enlace de firma por correo) */}
          <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50/60 p-3" data-testid="add-external-box">
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">Colaborador externo (por correo)</label>
            <div className="flex flex-col gap-2 sm:flex-row">
              <input value={extName} onChange={(e) => setExtName(e.target.value)} placeholder="Nombre (opcional)" data-testid="ext-name-input" className={`${inputCls} sm:max-w-[38%]`} />
              <input type="email" value={extEmail} onChange={(e) => setExtEmail(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addExternalByEmail(); } }} placeholder="correo@ejemplo.com" data-testid="ext-email-input" className={inputCls} />
              <button type="button" onClick={addExternalByEmail} data-testid="add-external-email-btn" className="shrink-0 rounded-lg bg-[#AC3333] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#FF0505]">Invitar</button>
            </div>
            <p className="mt-1.5 text-[11px] text-slate-400">Se enviará un correo a ese email con un enlace para firmar el acuerdo desde su Gmail.</p>
          </div>

          {/* Participants table (responsive: tarjetas apiladas en móvil, grid en desktop) */}
          {autofilled && (
            <div className="flex items-center justify-between gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700" data-testid="autofill-note">
              <span>Autollenamos tus datos legales desde tu último acuerdo. Revísalos antes de crear.</span>
              <button type="button" data-testid="autofill-clear" onClick={() => { setAutofilled(false); try { localStorage.removeItem(`afm_legal_${user.id}`); } catch { /* noop */ } setParticipants((prev) => prev.map((p) => p.user_id === user.id ? { ...p, id_document: "", nombre_artistico: "", tax_id: "", razon_social: "", country: "", address: "", publisher: "", ipi_autor: "", ipi_editora: "", pro: "" } : p)); }} className="shrink-0 font-semibold hover:underline">Limpiar</button>
            </div>
          )}
          <div className="overflow-hidden rounded-lg border border-slate-200">
            <div className="hidden grid-cols-[1fr_108px_72px_72px_28px] items-center gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500 sm:grid">
              <span>Participante</span><span className="text-center">Derechos</span><span className="text-center">Master %</span><span className="text-center">Publish %</span><span />
            </div>
            {participants.map((p, i) => (
              <div key={p.user_id} className="border-b border-slate-100 px-3 py-3 last:border-0 sm:grid sm:grid-cols-[1fr_108px_72px_72px_28px] sm:items-center sm:gap-2 sm:py-2" data-testid={`participant-row-${p.user_id}`}>
                <div className="min-w-0 space-y-1.5">
                  <div className="flex items-center gap-1.5">
                    <span className="truncate text-sm font-medium text-black">{p.name}</span>
                    {p.is_external && <span data-testid={`invited-badge-${p.user_id}`} className="shrink-0 rounded bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-700">Invitado</span>}
                  </div>
                  {p.is_external ? (
                    <div className="flex gap-1.5">
                      <input type="email" value={p.email} onChange={(e) => updateP(i, "email", e.target.value)} placeholder="correo para invitar (opcional)" data-testid={`ext-email-${p.user_id}`} className="min-w-0 flex-1 rounded border border-slate-300 px-1.5 py-0.5 text-xs" />
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      <input value={p.id_document} onChange={(e) => updateP(i, "id_document", e.target.value)} placeholder="Cédula / Documento *" data-testid={`doc-id-${p.user_id}`} className={`min-w-0 flex-1 rounded border px-1.5 py-0.5 text-xs ${(p.id_document || "").trim() ? "border-slate-300" : "border-red-300 bg-red-50"}`} />
                      <input type="email" value={p.email} onChange={(e) => updateP(i, "email", e.target.value)} placeholder="correo *" data-testid={`int-email-${p.user_id}`} className={`min-w-0 flex-1 rounded border px-1.5 py-0.5 text-xs ${isEmail(p.email) ? "border-slate-300" : "border-red-300 bg-red-50"}`} />
                    </div>
                  )}
                  <div className="flex flex-wrap gap-1" data-testid={`contrib-chips-${p.user_id}`}>
                    {(CONTRIB[docType] || []).map((c) => (
                      <button type="button" key={c} onClick={() => updateP(i, "contribution_role", c)} data-testid={`contrib-${c}-${p.user_id}`} className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold transition-colors ${p.contribution_role === c ? "border-[#AC3333] bg-[#AC3333] text-white" : "border-slate-300 text-slate-500 hover:border-[#AC3333]"}`}>{c}</button>
                    ))}
                  </div>
                </div>
                <div className="mt-2 grid grid-cols-[1fr_64px_64px_28px] items-center gap-2 sm:mt-0 sm:contents">
                  <select value={p.rights} onChange={(e) => updateP(i, "rights", e.target.value)} data-testid={`rights-${p.user_id}`} className="w-full rounded border border-slate-300 px-1.5 py-1 text-xs">
                    <option value="both">Ambos</option>
                    <option value="master">Master</option>
                    <option value="publishing">Publishing</option>
                  </select>
                  <input type="number" min="0" max="100" value={p.master_pct} disabled={!hasMaster(p)} onChange={(e) => updateP(i, "master_pct", e.target.value)} data-testid={`master-pct-${p.user_id}`} className="w-full rounded border border-slate-300 px-2 py-1 text-center font-mono text-sm disabled:bg-slate-100 disabled:text-slate-300" />
                  <input type="number" min="0" max="100" value={p.publishing_pct} disabled={!hasPub(p)} onChange={(e) => updateP(i, "publishing_pct", e.target.value)} data-testid={`pub-pct-${p.user_id}`} className="w-full rounded border border-slate-300 px-2 py-1 text-center font-mono text-sm disabled:bg-slate-100 disabled:text-slate-300" />
                  <button onClick={() => removeP(i)} disabled={participants.length === 1} className="flex justify-center text-slate-400 hover:text-red-600 disabled:opacity-30"><Trash2 className="h-4 w-4" /></button>
                </div>
                {(
                  <div className="sm:col-span-5">
                    <button type="button" data-testid={`legal-toggle-${p.user_id}`} onClick={() => setLegalOpen(legalOpen === i ? null : i)} className="text-[11px] font-semibold text-[#9E1B22] hover:underline">{legalOpen === i ? "Ocultar datos legales" : "Datos legales (opcional: nombre artístico, RIF, IPI, PRO…)"}</button>
                    {legalOpen === i && (
                      <div className="mt-2 grid gap-2 sm:grid-cols-3" data-testid={`legal-fields-${p.user_id}`}>
                        {[["nombre_artistico", "Nombre artístico"], ["tax_id", "RIF / Tax ID"], ["razon_social", "Razón social"], ["country", "País (ISO)"], ["address", "Dirección"], ["publisher", "Editorial"], ["ipi_autor", "IPI autor"], ["ipi_editora", "IPI editora"], ["pro", "PRO (SACVEN, ASCAP…)"]].map(([k, ph]) => (
                          <input key={k} data-testid={`legal-${k}-${p.user_id}`} value={p[k] || ""} onChange={(e) => updateP(i, k, e.target.value)} placeholder={ph} className="rounded border border-slate-300 px-2 py-1 text-xs" />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
            <div className="flex items-center justify-between gap-2 bg-slate-50 px-3 py-2 text-xs font-semibold sm:grid sm:grid-cols-[1fr_108px_72px_72px_28px] sm:items-center">
              <span className="text-slate-500">Total</span>
              <span className="hidden sm:block" />
              {showMaster ? <span className={`font-mono sm:text-center ${masterOk ? "text-emerald-600" : "text-red-600"}`} data-testid="master-sum"><span className="sm:hidden">M </span>{masterSum}%</span> : <span className="hidden sm:block" />}
              {showPub ? <span className={`font-mono sm:text-center ${pubOk ? "text-emerald-600" : "text-red-600"}`} data-testid="pub-sum"><span className="sm:hidden">P </span>{pubSum}%</span> : <span className="hidden sm:block" />}
              <span className="hidden sm:block" />
            </div>
          </div>
          <p className="text-xs text-slate-400">Elige los derechos de cada participante: <strong>Master</strong>, <strong>Publishing</strong> o <strong>Ambos</strong>. Master % suma 100% entre quienes tienen master; Publishing % suma 100% entre autores.</p>
        </div>
        <div className="flex justify-end gap-3 border-t border-slate-200 px-4 py-4 sm:px-6">
          <button onClick={onClose} className="shrink-0 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium hover:border-black">Cancelar</button>
          <button onClick={submit} disabled={!valid || saving} data-testid="submit-session-button" className="shrink-0 rounded-lg bg-black px-5 py-2 text-sm font-semibold text-white disabled:opacity-50">
            {saving ? "Creando…" : "Crear acuerdo"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SplitSheets() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [pending, setPending] = useState([]);
  const [tab, setTab] = useState("all");
  const [stats, setStats] = useState(null);
  const [status, setStatus] = useState("loading");
  const [dialog, setDialog] = useState(false);

  const load = () => {
    setStatus("loading");
    Promise.all([getSessions(user.id), getSessionStats(user.id).catch(() => null), getPendingSessions(user.id).catch(() => [])])
      .then(([s, st, p]) => { setSessions(Array.isArray(s) ? s : []); setStats(st); setPending(Array.isArray(p) ? p : []); setStatus("ok"); })
      .catch(() => setStatus("error"));
  };

  useEffect(load, [user.id]);
  const pendingIds = new Set(pending.map((p) => p.id));
  const list = tab === "pending" ? sessions.filter((s) => pendingIds.has(s.id)) : sessions;

  return (
    <div className="space-y-6" data-testid="split-sheets-page">
      <div className="flex items-center justify-between">
        <p className="max-w-xl text-sm text-slate-500">Acuerdos de colaboración entre artistas, productores y autores. Vincúlalos a un lanzamiento para activar el reparto de regalías.</p>
        <button onClick={() => setDialog(true)} data-testid="new-session-button" className="flex shrink-0 items-center gap-2 rounded-lg bg-black px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800">
          <Plus className="h-4 w-4" /> Nuevo acuerdo
        </button>
      </div>

      <details className="group" data-testid="splits-showcase-panel">
        <summary className="flex cursor-pointer list-none items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-black hover:border-black [&::-webkit-details-marker]:hidden">
          <span>Always Splits & Publishing · Simulador de reparto Master / Publishing</span>
          <span className="text-xs text-slate-500 group-open:hidden">Abrir</span><span className="hidden text-xs text-slate-500 group-open:inline">Cerrar</span>
        </summary>
        <div className="mt-3"><SplitsShowcase compact onCta={() => setDialog(true)} /></div>
      </details>

      {stats && stats.count_sealed > 0 && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4" data-testid="session-stats">
          <div className="rounded-xl border border-slate-200 bg-white p-4"><div className="text-xs text-slate-500">Sellados</div><div className="font-mono text-xl font-bold">{stats.count_sealed}</div></div>
          <div className="rounded-xl border border-slate-200 bg-white p-4"><div className="text-xs text-slate-500">Biométricos</div><div className="font-mono text-xl font-bold">{stats.biometric_count}</div></div>
          <div className="rounded-xl border border-slate-200 bg-white p-4"><div className="text-xs text-slate-500">Master acum.</div><div className="font-mono text-xl font-bold">{stats.total_master_pct}%</div></div>
          <div className="rounded-xl border border-slate-200 bg-white p-4"><div className="text-xs text-slate-500">Publishing acum.</div><div className="font-mono text-xl font-bold">{stats.total_publishing_pct}%</div></div>
        </div>
      )}

      {status === "loading" && <Loading />}
      {status === "error" && <ErrorState onRetry={load} />}
      {status === "ok" && sessions.length > 0 && (
        <div className="flex gap-1 rounded-xl border border-slate-200 bg-white p-1" data-testid="sessions-tabs">
          <button data-testid="sessions-tab-all" onClick={() => setTab("all")} className={`flex-1 rounded-lg px-3 py-2 text-xs font-semibold transition-colors ${tab === "all" ? "bg-black text-white" : "text-slate-600 hover:bg-slate-50"}`}>Todos <span className="opacity-60">({sessions.length})</span></button>
          <button data-testid="sessions-tab-pending" onClick={() => setTab("pending")} className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold transition-colors ${tab === "pending" ? "bg-black text-white" : "text-slate-600 hover:bg-slate-50"}`}>
            Por firmar {pending.length > 0 && <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${tab === "pending" ? "bg-white text-black" : "bg-[#9E1B22] text-white"}`} data-testid="sessions-pending-count">{pending.length}</span>}
          </button>
        </div>
      )}
      {status === "ok" && !sessions.length && (
        <EmptyState icon={Handshake} title="Sin acuerdos todavía" desc="Crea tu primer split sheet para formalizar la colaboración." testId="split-empty" />
      )}
      {status === "ok" && sessions.length > 0 && tab === "pending" && !list.length && (
        <EmptyState icon={Handshake} title="Nada pendiente de tu firma" desc="Todos tus acuerdos están firmados por tu parte." testId="split-pending-empty" />
      )}
      {status === "ok" && list.length > 0 && (
        <div className="space-y-3">
          {list.map((s) => {
            const p = pending.find((x) => x.id === s.id);
            return (
            <button key={s.id} data-testid={`session-card-${s.id}`} onClick={() => navigate(`/split-sheets/${s.id}`)} className="group flex w-full items-center justify-between rounded-xl border border-slate-200 bg-white p-4 text-left transition-all hover:border-black">
              <div>
                <div className="flex flex-wrap items-center gap-3">
                  <h3 className="font-serif text-lg font-bold text-black">{s.project_title}</h3>
                  <StatusBadge map={SESSION_STATUS} status={s.status} />
                  {p && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800" data-testid={`needs-sign-${s.id}`}>Tu firma pendiente · {p.pending_count}/{p.total_count} faltan</span>}
                </div>
                <div className="mt-1 text-xs text-slate-500">{(s.project_type || "").toUpperCase()} · {fmtDate(s.session_date)} · {(s.participants?.length || 0)} participantes</div>
              </div>
              <ChevronRight className="h-5 w-5 text-slate-300 transition-transform group-hover:translate-x-1 group-hover:text-black" />
            </button>
          ); })}
        </div>
      )}

      <CreateDialog open={dialog} onClose={() => setDialog(false)} onCreated={(res) => { setDialog(false); load(); if (res?.id) navigate(`/split-sheets/${res.id}`); }} />
    </div>
  );
}
